import React, { useMemo, useState } from "react";
import { ArrowRight, Users, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import TabSelector from "../common/TabSelector";
import { DataTable } from "../common";
import type { TableColumn } from "../common/Table";
import Badge from "../common/Badge";
import OverflowTooltip from "../common/OverflowTooltip";
import { TableSkeleton } from "../common/Skeleton";
import { CARD_CLASS } from "../../constants/dashboard";
import { getAvatarGradient, getInitials } from "../../utils/avatarUtils";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import {
  type IJoinerRow,
  type ITeamStatusMember,
  type TeamMemberStatus,
} from "../../types/teamStatus.types";
import {
  buildTeamStatusChips,
  filterTeamByStatus,
  getTeamStatusBadge,
  toTeamStatusRow,
} from "../../utils/teamStatusUtils";
import {
  useGetActiveUsersStatusQuery,
  useGetWorkforceAnalyticsQuery,
  useGetCelebrationsQuery,
  type IActiveUserStatusItem,
  type IRecentJoinerItem,
} from "../../store/apis/user.api";

export interface RecentJoinersCardProps {
  mode?: "tabs" | "joiners-only";
  title?: string;
}

type PanelTab = "live" | "joiners";

const TABS: { k: PanelTab; label: string }[] = [
  { k: "live", label: "Today's Status" },
  { k: "joiners", label: "Recent Joiners" },
];

type LiveRow = ReturnType<typeof toTeamStatusRow>;

const RecentJoinersCard: React.FC<RecentJoinersCardProps> = ({
  mode = "tabs",
  title,
}) => {
  const isJoinersOnly = mode === "joiners-only";
  const canViewEmployees = useHasPermission([
    PERMISSIONS.EMPLOYEE_MANAGEMENT_VIEW,
    PERMISSIONS.EMPLOYEE_MANAGEMENT_MANAGE,
  ]);

  const [activeTab, setActiveTab] = useState<PanelTab>(
    isJoinersOnly ? "joiners" : "live",
  );
  const [statusFilter, setStatusFilter] = useState<TeamMemberStatus | "all">(
    "all",
  );

  const { data: activeStatusData, isLoading: loadingStatus } =
    useGetActiveUsersStatusQuery(undefined, {
      skip: isJoinersOnly,
    });
  const { data: workforceAnalytics, isLoading: loadingAnalytics } =
    useGetWorkforceAnalyticsQuery(undefined, {
      skip: isJoinersOnly,
    });
  const { data: celebrationsData, isLoading: loadingCelebrations } =
    useGetCelebrationsQuery(undefined, {
      skip: !isJoinersOnly,
    });

  const teamStatusUsers: ITeamStatusMember[] = useMemo(() => {
    if (!activeStatusData?.users?.length) return [];

    return activeStatusData.users.map((user: IActiveUserStatusItem): ITeamStatusMember => {
      let status: TeamMemberStatus = "absent";
      if (user.status === "checked_in") status = "present";
      else if (user.status === "on_break") status = "break";
      else status = "absent";

      const checkInFormatted = user.checkInTime
        ? new Date(user.checkInTime).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
        : undefined;

      return {
        userId: user.userId,
        name: user.name || "Unknown",
        employeeId: user.employeeId,
        department: user.department || "General",
        role: undefined,
        checkInTime: checkInFormatted,
        detail:
          user.status === "checked_out"
            ? "Checked out"
            : user.status === "not_checked_in"
            ? "Not checked in"
            : undefined,
        status,
      };
    });
  }, [activeStatusData]);

  const joiners: IJoinerRow[] = useMemo(() => {
    if (isJoinersOnly) {
      if (!celebrationsData?.monthJoiners?.length) return [];
      return celebrationsData.monthJoiners.map((m, idx) => ({
        userId: m.employeeId ?? `joiner-${idx}`,
        name: m.name || "Unknown",
        role: "Team Member",
        department: "New Joiner",
        joinedDate: "This Month",
        status: "Active" as const,
        email: m.workEmail,
      }));
    }

    if (!workforceAnalytics?.recentJoiners?.length) return [];

    return workforceAnalytics.recentJoiners.map(
      (j: IRecentJoinerItem): IJoinerRow => ({
        userId: j.id,
        name: j.name,
        role: j.role || "Member",
        department: j.department || "General",
        joinedDate:
          j.joinedDateStr ||
          (j.joinedDate
            ? new Date(j.joinedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "Recent"),
        status:
          j.status === "active" || j.status === "Active"
            ? "Active"
            : "Inactive",
        email: j.email,
      }),
    );
  }, [isJoinersOnly, celebrationsData, workforceAnalytics]);

  const isLoading = isJoinersOnly
    ? loadingCelebrations
    : activeTab === "live"
    ? loadingStatus
    : loadingAnalytics;

  const chips = useMemo(
    () => buildTeamStatusChips(teamStatusUsers),
    [teamStatusUsers],
  );

  const liveRows: LiveRow[] = useMemo(
    () =>
      filterTeamByStatus(teamStatusUsers, statusFilter).map(toTeamStatusRow),
    [teamStatusUsers, statusFilter],
  );

  const liveColumns: TableColumn<LiveRow>[] = useMemo(
    () => [
      {
        key: "employee",
        title: "Employee",
        required: true,
        width: 220,
        render: (_, r) => (
          <div className="flex items-center gap-3">
            <div
              className="grid h-9 w-9 flex-none place-items-center rounded-avatar text-body-xs font-bold text-white shadow-xs"
              style={{ background: r.avatarBg }}
            >
              {r.initials}
            </div>
            <div className="min-w-0">
              <OverflowTooltip className="block truncate text-body-md font-bold text-text-primary">
                {r.name}
              </OverflowTooltip>
              <OverflowTooltip className="mt-0.5 block truncate text-body-xs text-text-tertiary">
                {r.role ?? r.department}
              </OverflowTooltip>
            </div>
          </div>
        ),
      },
      {
        key: "department",
        title: "Department",
        hideBelow: "md",
        width: 150,
        render: (_, r) => (
          <OverflowTooltip className="block truncate text-body-sm text-text-secondary">
            {r.department}
          </OverflowTooltip>
        ),
      },
      {
        key: "checkin",
        title: "Check-in",
        hideBelow: "md",
        width: 170,
        render: (_, r) => (
          <div className="min-w-0">
            <div className="text-body-sm font-semibold text-text-secondary">
              {r.checkInTime ?? "—"}
            </div>
            {r.detail && (
              <OverflowTooltip className="block truncate text-body-xs text-text-tertiary">
                {r.detail}
              </OverflowTooltip>
            )}
          </div>
        ),
      },
      {
        key: "status",
        title: "Status",
        width: 130,
        render: (_, r) => {
          const badge = getTeamStatusBadge(r.status);
          return <Badge variant={badge.variant}>{badge.label}</Badge>;
        },
      },
    ],
    [],
  );

  const joinerColumns: TableColumn<IJoinerRow>[] = useMemo(
    () => [
      {
        key: "employee",
        title: "Employee",
        required: true,
        width: 220,
        render: (_, r) => (
          <div className="flex items-center gap-3">
            <div
              className="grid h-9 w-9 flex-none place-items-center rounded-avatar text-body-xs font-bold text-white shadow-xs"
              style={{ background: getAvatarGradient(r.name) }}
            >
              {getInitials(r.name)}
            </div>
            <div className="min-w-0">
              <OverflowTooltip className="block truncate text-body-md font-bold text-text-primary">
                {r.name}
              </OverflowTooltip>
              <OverflowTooltip className="block truncate text-body-xs text-text-tertiary">
                {r.role}
              </OverflowTooltip>
            </div>
          </div>
        ),
      },
      {
        key: "department",
        title: "Department",
        hideBelow: "md",
        width: 150,
        render: (_, r) => (
          <OverflowTooltip className="block truncate text-body-sm text-text-secondary">
            {r.department}
          </OverflowTooltip>
        ),
      },
      {
        key: "joined",
        title: "Joined",
        hideBelow: "md",
        width: 130,
        render: (_, r) => (
          <span className="whitespace-nowrap text-body-sm font-medium text-text-strong">
            {r.joinedDate}
          </span>
        ),
      },
      {
        key: "status",
        title: "Status",
        width: 120,
        render: (_, r) => (
          <Badge variant={r.status === "Active" ? "green" : "red"}>
            {r.status}
          </Badge>
        ),
      },
    ],
    [],
  );

  if (isJoinersOnly) {
    return (
      <div className={`${CARD_CLASS} p-4 sm:p-5 h-full min-h-[270px] flex flex-col`}>
        {/* Header matching Team Status */}
        <div className="flex items-center justify-between mb-3.5 shrink-0 gap-2">
          <h3 className="m-0 text-heading-sm font-bold text-text-primary flex items-center gap-2 tracking-tight truncate">
            <UserPlus className="w-[18px] h-[18px] text-primary-600 flex-none" />
            <span>{title || "Recent Joiners"}</span>
          </h3>
          {canViewEmployees && (
            <Link
              to="/employees"
              className="flex flex-none items-center gap-1 rounded text-body-sm font-bold text-primary-700 no-underline transition-colors hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              View all
              <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          )}
        </div>

        {/* Content: 2-column layout (Employee on left, Department on right), NO horizontal scroll */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-border-input pr-1 overflow-x-hidden">
          {isLoading ? (
            <div className="space-y-2">
              <TableSkeleton rows={4} columns={2} />
            </div>
          ) : joiners.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-4">
              <div className="w-12 h-12 bg-layout-hover rounded-full grid place-items-center mx-auto mb-2 text-text-tertiary">
                <Users className="w-5 h-5" />
              </div>
              <h4 className="text-body-sm font-semibold text-text-primary mb-0.5">
                No Recent Joiners
              </h4>
              <p className="text-caption text-text-tertiary">
                No new team members joined this month.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {joiners.map((joiner, index) => {
                const initial = getInitials(joiner.name);
                return (
                  <div
                    key={joiner.userId ?? `${joiner.name}-${index}`}
                    className="flex items-center justify-between gap-3 p-2.5 rounded-xl border border-border-secondary bg-surface hover:bg-layout-hover transition-all duration-150 group"
                  >
                    {/* Left: Employee Avatar + Name + Subtitle */}
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className="w-10 h-10 rounded-xl grid place-items-center text-white font-bold text-xs flex-none shadow-xs"
                        style={{ background: getAvatarGradient(joiner.name) }}
                      >
                        {initial}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-body-sm font-bold text-text-primary truncate group-hover:text-primary-700 transition-colors">
                          {joiner.name}
                        </p>
                        <p className="text-caption text-text-tertiary mt-0.5 truncate">
                          {joiner.role && joiner.role !== "Team Member"
                            ? joiner.role
                            : joiner.email || "New Joiner"}
                        </p>
                      </div>
                    </div>

                    {/* Right: Department Tag */}
                    <div className="flex-none">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-layout-hover text-text-secondary border border-border-secondary max-w-[120px] truncate">
                        {joiner.department || "General"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[280px] flex-col overflow-hidden rounded-card border border-border-secondary bg-surface shadow-soft">
      <div className="flex flex-wrap items-center justify-between gap-3 p-5 pb-3">
        <TabSelector
          tabs={TABS}
          active={activeTab}
          onChange={setActiveTab}
          size="sm"
        />
        {canViewEmployees && (
          <Link
            to="/employees"
            className="flex flex-none items-center gap-1 rounded text-body-sm font-bold text-primary-700 no-underline transition-colors hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            View all
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="px-5 pb-5">
          <TableSkeleton rows={5} columns={4} />
        </div>
      ) : activeTab === "live" ? (
        <>
          <div
            className="flex flex-wrap gap-2 px-5 pb-3"
            role="group"
            aria-label="Filter by status"
          >
            {chips.map((chip) => {
              const selected = statusFilter === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setStatusFilter(chip.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-body-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    selected
                      ? "border-primary-300 bg-primary-50 text-primary-700"
                      : "border-border-secondary bg-surface text-text-secondary hover:bg-layout-hover"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 flex-none rounded-full ${chip.dotClass}`}
                    aria-hidden="true"
                  />
                  {chip.label}
                  <span className="tabular-nums text-text-tertiary">
                    {chip.count}
                  </span>
                </button>
              );
            })}
          </div>
          {liveRows.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
              <h4 className="text-body-md font-bold text-text-primary">
                No employees in this status right now
              </h4>
              <p className="mt-1 text-body-sm text-text-tertiary">
                Try another status filter.
              </p>
            </div>
          ) : (
            <div className="max-h-[352px] flex-1 overflow-y-auto">
              <DataTable
                columns={liveColumns}
                rows={liveRows}
                rowKey={(r) => r.userId}
                emptyMessage="No employees in this status right now."
                responsive="scroll"
                minWidth={670}
              />
            </div>
          )}
        </>
      ) : joiners.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
          <h4 className="text-body-md font-bold text-text-primary">
            No Recent Joiners
          </h4>
          <p className="mt-1 text-body-sm text-text-tertiary">
            No new team members joined this month.
          </p>
        </div>
      ) : (
        <div className="max-h-[352px] flex-1 overflow-y-auto">
          <DataTable
            columns={joinerColumns}
            rows={joiners}
            rowKey={(r, i) => r.userId ?? r.email ?? `${r.name}-${i}`}
            emptyMessage="No new team members joined this month."
            responsive="scroll"
            minWidth={620}
          />
        </div>
      )}
    </div>
  );
};

export default RecentJoinersCard;
